import { CONSENT_TIMING_PREFIX } from '../consent/consentTiming'
import { firstAdRequestAt } from '../consent/firstAdRequest'
import { READINESS_MARK_PREFIX } from './readinessMilestones'

/**
 * Where a page load's first visible ad spent its time, as context on the
 * `first_ad_visible` vital, so one load can be broken down stage by stage. Each
 * stage is in ms from the same page start as the vital: the start of the
 * navigation, or the moment a prerendered page was shown. A stage from before
 * that counts as 0, and a stage that did not happen is left out.
 *
 * Page stages, read from the performance marks the page load leaves:
 *
 * - `app_mounted_ms`: the app finished mounting, its async setup and, on a
 *   server render, hydration included (Nuxt's `app:suspense:resolve`).
 * - `first_slot_mount_ms`: the first display slot mounted and started waiting
 *   for AY.
 * - `ay_ready_ms`, `gpt_ready_ms`: the readiness milestones.
 * - `first_ad_request_ms`: the first ad request from any provider.
 * - `first_auction_start_ms`, `first_auction_end_ms`: the first prebid
 *   auction's auctionInit and auctionEnd, by the same count as the
 *   `prebid_auction` vital. A prerendered page counts its auctions from when it
 *   was shown.
 *
 * Stages of the ad that became visible:
 *
 * - `slot_request_ms`: its GPT request (display only).
 * - `rendered_ms`: when it rendered. For a native widget, when it reported its
 *   render. For Primis, when the player tag loaded.
 * - `in_view_ms`: when it was both rendered and half in view, the moment the
 *   vital ends.
 * - `in_view_wait_ms`: `in_view_ms` less `rendered_ms`, the time a rendered ad
 *   waited to come into view, mostly the visitor scrolling to it. 0 when it was
 *   already in view. For Primis it also holds the player laying itself out.
 */

const STAGE_PREFIX = 'first-ad-stage:'

export type FirstAdStageMark =
  | 'app-mounted'
  | 'first-auction-start'
  | 'first-auction-end'

const PAGE_STAGES: ReadonlyArray<[field: string, mark: string]> = [
  ['app_mounted_ms', `${STAGE_PREFIX}app-mounted`],
  [
    'first_slot_mount_ms',
    `${CONSENT_TIMING_PREFIX}first-visible-ad-wait-start`,
  ],
  ['ay_ready_ms', `${READINESS_MARK_PREFIX}ay_ready`],
  ['gpt_ready_ms', `${READINESS_MARK_PREFIX}gpt_ready`],
  ['first_auction_start_ms', `${STAGE_PREFIX}first-auction-start`],
  ['first_auction_end_ms', `${STAGE_PREFIX}first-auction-end`],
]

/** Stages of the ad that became visible, on the `performance.now()` clock. */
export interface AdVisibleTiming {
  requestedAt?: number
  renderedAt?: number
  inViewAt?: number
}

const recordedMarks = new Set<string>()

/**
 * Leaves `first-ad-stage:<name>`, once per page load, at `at` on the
 * `performance.now()` clock or now. A browser that ignores the mark's start
 * time would put it at the wrong moment, so the mark is dropped there instead.
 * Only a measurement: it never throws.
 */
export function markFirstAdStage(name: FirstAdStageMark, at?: number): void {
  const markName = `${STAGE_PREFIX}${name}`
  if (recordedMarks.has(markName)) return
  try {
    const startTime = at ?? performance.now()
    const mark: PerformanceMark | undefined = performance.mark(markName, {
      startTime,
    })
    if (mark?.startTime !== startTime) {
      performance.clearMarks(markName)
      return
    }
    recordedMarks.add(markName)
  } catch {
    // Lose the stage, keep the page.
  }
}

const sinceShown = (at: number, shownAt: number): number =>
  Math.round(Math.max(0, at - shownAt))

const markTime = (name: string): number | undefined =>
  performance.getEntriesByName(name, 'mark')[0]?.startTime

/**
 * The stage fields for the vital, each in ms from `shownAt`, the moment the
 * visitor started seeing the page on the `performance.now()` clock. Empty when
 * they cannot be read, so the vital still goes out.
 */
export function firstAdStageContext(
  shownAt: number,
  timing: AdVisibleTiming = {}
): Record<string, number> {
  try {
    const context: Record<string, number> = {}
    const add = (field: string, at: number | undefined) => {
      if (at !== undefined && Number.isFinite(at)) {
        context[field] = sinceShown(at, shownAt)
      }
    }
    for (const [field, mark] of PAGE_STAGES) {
      add(field, markTime(mark))
    }
    add('first_ad_request_ms', firstAdRequestAt())
    add('slot_request_ms', timing.requestedAt)
    add('rendered_ms', timing.renderedAt)
    add('in_view_ms', timing.inViewAt)
    // From the two fields as sent, so a render before the page was shown
    // does not count time the visitor never saw.
    const { rendered_ms: rendered, in_view_ms: inView } = context
    if (rendered !== undefined && inView !== undefined) {
      context.in_view_wait_ms = Math.max(0, inView - rendered)
    }
    return context
  } catch {
    return {}
  }
}

export function resetFirstAdStagesForTests(): void {
  recordedMarks.clear()
}
